import React, { ReactElement } from 'react';
import { ImageOutput } from 'js/model/rainbow/ImageOutput';
import { BestForBrowseVenueData } from 'js/model/rainbow/best-for-browse-page/BestForBrowsePageVenueData';
import { Caption } from 'js/pages/BestForBrowsePage/Venue/Caption';
import { ResizingImage } from 'js/components/ResizingImage/ResizingImage';
import { trackVenueResultData } from 'js/pages/BrowsePage/BrowseResult/tracking';
import styles from './Venue.module.css';

export enum ClickTarget {
  FindOutMore = 'findOutMore',
  Caption = 'caption',
}

interface Props {
  position: number;
  name: string;
  location: string;
  description: string;
  images: ImageOutput[];
  url: string;
  findOutMoreLinkLabel: string;
  // a rating component is supposed to be passed as child
  children: React.ReactNode;
  onClick?: (
    event: React.SyntheticEvent<HTMLAnchorElement>,
    clickTarget: ClickTarget
  ) => void;
}

export function Venue(props: Props): ReactElement {
  let firstImage: ImageOutput | undefined;
  if (props.images.length > 0) {
    firstImage = props.images[0];
  }

  return (
    <div>
      {firstImage && (
        <div className={styles.image}>
          <ResizingImage imageUris={firstImage.uris} upscaleAllowance={0} />
        </div>
      )}
      <div className={styles.captionAndRating}>
        <div className={styles.caption}>
          <Caption
            position={props.position}
            name={props.name}
            nameHref={props.url}
            nameOnClick={(event) => props.onClick?.(event, ClickTarget.Caption)}
            location={props.location}
          />
        </div>
        <div className={styles.rating}>{props.children}</div>
      </div>
      <div
        className={styles.description}
        dangerouslySetInnerHTML={{ __html: props.description }}
      />
      <a
        className={styles.findOutMoreLinkLabel}
        href={props.url}
        onClick={(event) => props.onClick?.(event, ClickTarget.FindOutMore)}
      >
        {props.findOutMoreLinkLabel}
      </a>
    </div>
  );
}

type GenerateUriFunction = (
  pageType: 'venue',
  values: { normalisedName: [string] }
) => string;

export function createVenueViewModel(
  data: BestForBrowseVenueData,
  cms: { 'find-out-more': string },
  position: number,
  generateUriFunction: GenerateUriFunction,
  trackingFunction: (
    event: React.SyntheticEvent<HTMLAnchorElement>,
    clickTarget: ClickTarget,
    venueId: number
  ) => void,
  countryCode: string
): Omit<Props, 'children'> {
  return {
    position,
    name: data.name,
    location: data.location,
    description: data.description,
    images: data.images,
    url: generateUriFunction('venue', {
      normalisedName: [data.normalisedName],
    }),
    findOutMoreLinkLabel: cms['find-out-more'],
    onClick: (
      event: React.SyntheticEvent<HTMLAnchorElement>,
      clickTarget: ClickTarget
    ) => {
      trackingFunction(event, clickTarget, data.venueId);
      trackVenueResultData({
        eventName: 'search_venue_clicked',
        venueId: data.venueId,
        rating: {
          average: 0, // adding this for typescript purposes. It is not used in the tracking function
          count: data.rating.count,
          displayAverage: data.rating.displayAverage,
        },
        countryCode,
        venueRank: position,
        pageName: 'best_for_browse',
      });
    },
  };
}
